import {
  Paper,
  Box,
  Typography,
  IconButton,
  Button,
  Chip,
  Divider,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import {
  Save,
  Undo,
  ChevronLeft as ChevronLeftIcon,
  Menu as MenuIcon,
  ContentCopy,
  ContentPaste,
  Close,
  PictureAsPdf,
  TableChart,
} from "@mui/icons-material";
import { Shop } from "../../types/shop.types";
import {
  ScheduleChange,
  CopiedCellData,
  CopiedEmployeeWeekData,
  CopiedShopWeekData,
  CopiedEntireWeekData,
} from "../../types/scheduleManager.types";
import { formatDateRange } from "../../utils/scheduleHelpers";

interface SchedulesSidebarProps {
  sidebarOpen: boolean;
  onToggleSidebar: () => void;
  // Data
  shops: Shop[];
  // Change tracking
  hasChanges: boolean;
  changesCount: number;
  hasErrors: boolean;
  errorsCount: number;
  localChanges: Record<string, ScheduleChange>;
  // Actions
  onSave: () => void;
  onRevertAll: () => void;
  onRevertChange: (shopId: string, employeeId: string, date: Date) => void;
  isSaving: boolean;
  // Clipboard state
  copiedCellData: CopiedCellData | null;
  copiedEmployeeWeekData: CopiedEmployeeWeekData | null;
  copiedShopWeekData: CopiedShopWeekData | null;
  copiedEntireWeekData: CopiedEntireWeekData | null;
  // Clipboard actions
  onCopyWeek: () => void;
  onPasteWeek: () => void;
  onClearClipboard: () => void;
  // Export actions
  onExportPDF: () => void;
  onExportCSV: () => void;
}

export default function SchedulesSidebar({
  sidebarOpen,
  onToggleSidebar,
  shops,
  hasChanges,
  changesCount,
  hasErrors,
  errorsCount,
  localChanges,
  onSave,
  onRevertAll,
  onRevertChange,
  isSaving,
  copiedCellData,
  copiedEmployeeWeekData,
  copiedShopWeekData,
  copiedEntireWeekData,
  onCopyWeek,
  onPasteWeek,
  onClearClipboard,
  onExportPDF,
  onExportCSV,
}: SchedulesSidebarProps) {
  const hasClipboardData =
    copiedCellData ||
    copiedEmployeeWeekData ||
    copiedShopWeekData ||
    copiedEntireWeekData;

  // Render minimized sidebar
  if (!sidebarOpen) {
    return (
      <Paper
        sx={{
          position: "fixed",
          left: 0,
          top: 64,
          bottom: 0,
          width: 64,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          py: 2,
          zIndex: 10,
          borderRadius: 0,
        }}
      >
        <Tooltip title="Expand Panel" placement="right">
          <IconButton onClick={onToggleSidebar} sx={{ mb: 2 }}>
            <MenuIcon />
          </IconButton>
        </Tooltip>

        <Divider sx={{ width: "100%", my: 1 }} />

        <Tooltip
          title={
            hasErrors
              ? `Cannot save: ${errorsCount} error${errorsCount > 1 ? "s" : ""} to fix`
              : "Save Changes"
          }
          placement="right"
        >
          <span>
            <IconButton
              onClick={onSave}
              disabled={!hasChanges || isSaving || hasErrors}
              color={hasChanges && !hasErrors ? "primary" : "default"}
              sx={{ mb: 1 }}
            >
              <Save />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title="Revert Changes" placement="right">
          <span>
            <IconButton
              onClick={onRevertAll}
              disabled={!hasChanges}
              color={hasChanges ? "warning" : "default"}
            >
              <Undo />
            </IconButton>
          </span>
        </Tooltip>

        {/* Show error chip if errors exist, otherwise show unsaved count */}
        {hasErrors ? (
          <Chip
            label={errorsCount}
            size="small"
            color="error"
            sx={{ mt: 1, minWidth: 24, height: 24 }}
          />
        ) : hasChanges ? (
          <Chip
            label={changesCount}
            size="small"
            color="warning"
            sx={{ mt: 1, minWidth: 24, height: 24 }}
          />
        ) : null}

        <Divider sx={{ width: "100%", my: 1 }} />

        {/* Export buttons */}
        <Tooltip title="Export as PDF" placement="right">
          <IconButton onClick={onExportPDF} sx={{ mb: 1 }}>
            <PictureAsPdf />
          </IconButton>
        </Tooltip>

        <Tooltip title="Export as CSV" placement="right">
          <IconButton onClick={onExportCSV}>
            <TableChart />
          </IconButton>
        </Tooltip>

        <Divider sx={{ width: "100%", my: 1 }} />

        {/* Copy Entire Week button */}
        <Tooltip title="Copy Entire Week" placement="right">
          <IconButton onClick={onCopyWeek} sx={{ mb: 1 }}>
            <ContentCopy />
          </IconButton>
        </Tooltip>

        {/* Paste Entire Week button */}
        <Tooltip
          title={copiedEntireWeekData ? "Paste Entire Week" : "Nothing copied"}
          placement="right"
        >
          <span>
            <IconButton
              onClick={onPasteWeek}
              color={copiedEntireWeekData ? "info" : "default"}
              disabled={!copiedEntireWeekData}
            >
              <ContentPaste />
            </IconButton>
          </span>
        </Tooltip>

        {/* Copy/Paste indicator */}
        {hasClipboardData && (
          <Tooltip
            title={
              copiedEntireWeekData
                ? `Entire week copied`
                : copiedShopWeekData
                  ? `Shop copied: ${copiedShopWeekData.shopName}`
                  : copiedCellData
                    ? `Day copied: ${copiedCellData.employeeName}`
                    : `Week copied: ${copiedEmployeeWeekData?.employeeName}`
            }
            placement="right"
          >
            <Chip
              size="small"
              color="info"
              label={
                copiedEntireWeekData
                  ? "All"
                  : copiedShopWeekData
                    ? "Shop"
                    : copiedCellData
                      ? "Day"
                      : "Week"
              }
              sx={{ mt: 1 }}
            />
          </Tooltip>
        )}
      </Paper>
    );
  }

  // Render expanded sidebar
  return (
    <Paper
      sx={{
        position: "fixed",
        left: 0,
        top: 64,
        bottom: 0,
        width: 280,
        p: 2,
        overflow: "auto",
        zIndex: 10,
        borderRadius: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Typography variant="subtitle1" fontWeight={600}>
          Actions
        </Typography>
        <IconButton size="small" onClick={onToggleSidebar}>
          <ChevronLeftIcon fontSize="small" />
        </IconButton>
      </Box>

      <Divider sx={{ mb: 2 }} />

      {/* Save/Revert Section */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Typography variant="overline" color="text.secondary">
          Changes
        </Typography>
        {hasErrors ? (
          <Chip
            label={`${errorsCount} error${errorsCount > 1 ? "s" : ""}`}
            size="small"
            color="error"
          />
        ) : hasChanges ? (
          <Chip label={`${changesCount} unsaved`} size="small" color="warning" />
        ) : (
          <Typography variant="caption" color="text.secondary">
            No changes
          </Typography>
        )}
      </Box>

      <Tooltip
        title={
          hasErrors
            ? `Fix ${errorsCount} error${errorsCount > 1 ? "s" : ""} before saving`
            : ""
        }
        placement="top"
      >
        <span>
          <Button
            variant="contained"
            startIcon={isSaving ? <CircularProgress size={16} /> : <Save />}
            onClick={onSave}
            disabled={!hasChanges || isSaving || hasErrors}
            fullWidth
            sx={{ mb: 1 }}
          >
            {isSaving ? "Saving..." : "Save Changes"}
          </Button>
        </span>
      </Tooltip>

      <Button
        variant="outlined"
        startIcon={<Undo />}
        onClick={onRevertAll}
        disabled={!hasChanges}
        fullWidth
        color="warning"
        sx={{ mb: 3 }}
      >
        Revert Changes
      </Button>

      <Divider sx={{ mb: 2 }} />

      {/* Export Section */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Typography variant="overline" color="text.secondary">
          Export
        </Typography>
      </Box>

      <Box sx={{ display: "flex", gap: 1, mb: 3 }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<PictureAsPdf />}
          onClick={onExportPDF}
          fullWidth
          color="error"
        >
          PDF
        </Button>
        <Button
          variant="outlined"
          size="small"
          startIcon={<TableChart />}
          onClick={onExportCSV}
          fullWidth
          color="success"
        >
          CSV
        </Button>
      </Box>

      <Divider sx={{ mb: 2 }} />

      {/* Copy Entire Week Section */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Typography variant="overline" color="text.secondary">
          Copy / Paste
        </Typography>
      </Box>

      <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<ContentCopy />}
          onClick={onCopyWeek}
          fullWidth
        >
          Copy Week
        </Button>
        <Tooltip
          title={copiedEntireWeekData ? "" : "Nothing copied"}
          placement="top"
        >
          <span style={{ width: "100%" }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<ContentPaste />}
              onClick={onPasteWeek}
              fullWidth
              color={copiedEntireWeekData ? "info" : "inherit"}
              disabled={!copiedEntireWeekData}
            >
              Paste Week
            </Button>
          </span>
        </Tooltip>
      </Box>

      <Divider sx={{ mb: 2 }} />

      {/* Clipboard Section */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Typography variant="overline" color="text.secondary">
          Clipboard
        </Typography>
        {hasClipboardData && (
          <Tooltip title="Clear clipboard">
            <IconButton size="small" onClick={onClearClipboard}>
              <Close fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {copiedEntireWeekData ? (
        <Box
          sx={{
            p: 1.5,
            borderRadius: 1,
            bgcolor: "info.50",
            border: "1px solid",
            borderColor: "info.main",
            mb: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            <ContentCopy fontSize="small" color="info" />
            <Typography variant="body2" fontWeight={500}>
              Entire Week Copied
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary" display="block">
            {formatDateRange(
              copiedEntireWeekData.weekStartDate,
              copiedEntireWeekData.weekEndDate
            )}{" "}
            (Week {copiedEntireWeekData.weekNumber})
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {Object.keys(copiedEntireWeekData.shops).length} shop(s)
          </Typography>
        </Box>
      ) : copiedShopWeekData ? (
        <Box
          sx={{
            p: 1.5,
            borderRadius: 1,
            bgcolor: "info.50",
            border: "1px solid",
            borderColor: "info.main",
            mb: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            <ContentCopy fontSize="small" color="info" />
            <Typography variant="body2" fontWeight={500}>
              Shop Week Copied
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary" display="block">
            {formatDateRange(
              copiedShopWeekData.weekStartDate,
              copiedShopWeekData.weekEndDate
            )}{" "}
            (Week {copiedShopWeekData.weekNumber})
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {copiedShopWeekData.shopName} •{" "}
            {Object.keys(copiedShopWeekData.employeeSchedules).length}{" "}
            employee(s)
          </Typography>
        </Box>
      ) : copiedCellData ? (
        <Box
          sx={{
            p: 1.5,
            borderRadius: 1,
            bgcolor: "info.50",
            border: "1px solid",
            borderColor: "info.main",
            mb: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            <ContentCopy fontSize="small" color="info" />
            <Typography variant="body2" fontWeight={500}>
              Day Copied
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary" display="block">
            {new Date(copiedCellData.dateKey).toLocaleDateString("en-US", {
              weekday: "short",
              day: "numeric",
              month: "short",
              year: "numeric",
            })}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {copiedCellData.employeeName} • {copiedCellData.workingHours.length}{" "}
            shift(s)
          </Typography>
        </Box>
      ) : copiedEmployeeWeekData ? (
        <Box
          sx={{
            p: 1.5,
            borderRadius: 1,
            bgcolor: "info.50",
            border: "1px solid",
            borderColor: "info.main",
            mb: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            <ContentCopy fontSize="small" color="info" />
            <Typography variant="body2" fontWeight={500}>
              Week Copied
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary" display="block">
            {formatDateRange(
              copiedEmployeeWeekData.weekStartDate,
              copiedEmployeeWeekData.weekEndDate
            )}{" "}
            (Week {copiedEmployeeWeekData.weekNumber})
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {copiedEmployeeWeekData.employeeName} •{" "}
            {Object.keys(copiedEmployeeWeekData.weekData).length} day(s)
          </Typography>
        </Box>
      ) : (
        <Typography
          variant="body2"
          color="text.disabled"
          fontStyle="italic"
          sx={{ mb: 2 }}
        >
          Use copy buttons on rows/cells to copy schedules
        </Typography>
      )}

      <Divider sx={{ mb: 2 }} />

      {/* Modified Cells List */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Typography variant="overline" color="text.secondary">
          Modified Cells
        </Typography>
      </Box>

      <Box sx={{ flexGrow: 1, overflow: "auto" }}>
        {Object.keys(localChanges).length === 0 ? (
          <Typography variant="body2" color="text.disabled" fontStyle="italic">
            No pending changes
          </Typography>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {Object.values(localChanges).map((change) => {
              const shop = shops.find((s) => s.id === change.shopId);
              return (
                <Box
                  key={`${change.shopId}_${change.employeeId}_${change.dateKey}`}
                  sx={{
                    p: 1.5,
                    borderRadius: 1,
                    bgcolor: "action.hover",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box sx={{ overflow: "hidden", flex: 1 }}>
                    <Typography variant="body2" fontWeight={500} noWrap>
                      {change.employeeName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {shop?.name} • {change.dateKey}
                    </Typography>
                  </Box>
                  <Tooltip title="Revert this change">
                    <IconButton
                      size="small"
                      onClick={() => {
                        const date = new Date(change.dateKey);
                        onRevertChange(change.shopId, change.employeeId, date);
                      }}
                      color="warning"
                    >
                      <Undo fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>

      {/* Help Text */}
      <Box sx={{ mt: "auto", pt: 2 }}>
        <Divider sx={{ mb: 2 }} />
        <Typography variant="caption" color="text.secondary">
          Click on a time chip to edit. Use the + button to add shifts. Changes
          are saved when you click "Save Changes".
        </Typography>
      </Box>
    </Paper>
  );
}
